import { Plus, Sparkles } from 'lucide-react';
import ReactSelect from 'react-select';
import type { ProjectType } from '../../../../../services/projectTypeService';
import ProjectTypeCards from '../components/ProjectTypeCards';
import { selectSovereignStyles } from '../selectStyles';
import type { WizardOption } from '../types';
import { FieldLabel, HelperText, svInput } from '../ui';

export default function BasicsStep(props: {
  code: string;
  setCode: (v: string) => void;
  onAutoCode: () => void;
  codeError: string | null;
  name: string;
  setName: (v: string) => void;
  projectTypes: ProjectType[];
  projectTypeId: string;
  setProjectTypeId: (v: string) => void;
  customerOptions: WizardOption[];
  customerId: string;
  setCustomerId: (v: string) => void;
  loadingMasters: boolean;
  onAddCustomer: () => void;
}) {
  const {
    code, setCode, onAutoCode, codeError, name, setName,
    projectTypes, projectTypeId, setProjectTypeId,
    customerOptions, customerId, setCustomerId, loadingMasters, onAddCustomer,
  } = props;

  return (
    <div className="space-y-6">
      {/* Code first (short), then name (wide) */}
      <div className="flex flex-col gap-4 sm:flex-row">
        <div className="sm:w-52 sm:shrink-0">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <label className="text-[11px] font-bold uppercase tracking-[0.12em] text-[#2A1F17]">Project code</label>
            <button
              type="button"
              onClick={onAutoCode}
              className="inline-flex items-center gap-1 rounded-lg px-1.5 py-0.5 text-[10px] font-bold transition"
              style={{ background: 'rgba(51,146,116,0.12)', color: '#1F5A47' }}
            >
              <Sparkles className="h-3 w-3" />
              Auto
            </button>
          </div>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            className={`${svInput} h-12 font-mono text-sm font-bold uppercase tracking-[0.08em] tabular-nums`}
            style={codeError ? { borderColor: '#9F1239' } : undefined}
            placeholder="SOL-MH-042"
            required
            maxLength={50}
            autoComplete="off"
          />
          {codeError ? (
            <HelperText tone="warn">{codeError}</HelperText>
          ) : (
            <HelperText tone="info">Unique short identifier</HelperText>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <FieldLabel hint="Required">Project name</FieldLabel>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={`${svInput} h-12 text-[15px]`}
            placeholder="e.g. 25.4 MW Ganga Solar Park — Phase II"
            required
            maxLength={300}
          />
          <HelperText>Full descriptive name as it appears on contracts and reports.</HelperText>
        </div>
      </div>

      {/* Type before customer */}
      <div>
        <FieldLabel hint="Required — choose first">Project type</FieldLabel>
        <ProjectTypeCards types={projectTypes} value={projectTypeId} onChange={setProjectTypeId} loading={loadingMasters} />
        <HelperText>Type decides which capacity fields apply and the project's visual theme.</HelperText>
      </div>

      <div className="max-w-xl">
        <FieldLabel hint="Optional">Customer</FieldLabel>
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <ReactSelect<WizardOption, false>
              options={customerOptions}
              value={customerOptions.find((o) => o.value === customerId) ?? null}
              placeholder={!projectTypeId ? 'Select a project type first' : loadingMasters ? 'Loading…' : 'Select customer'}
              onChange={(opt) => setCustomerId(opt?.value ?? '')}
              isDisabled={loadingMasters || !projectTypeId}
              isClearable
              styles={selectSovereignStyles<WizardOption, false>()}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            />
          </div>
          <button
            type="button"
            onClick={onAddCustomer}
            disabled={!projectTypeId}
            title="Add new customer"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl transition disabled:opacity-40"
            style={{ background: '#fcfaf3', border: '1px solid rgba(11,59,46,0.28)', color: '#1F5A47' }}
          >
            <Plus className="h-4 w-4" strokeWidth={2.5} />
          </button>
        </div>
        {!projectTypeId ? <HelperText>Pick the project type above to unlock customer selection.</HelperText> : null}
      </div>
    </div>
  );
}
